import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import TopHeader from '../components/TopHeader';
import { accountingAPI, visaProgramAPI } from '../utils/api';
import {
  DollarSign, Plus, Search, Download, Edit2, Trash2,
  Eye, Calendar, ChevronLeft, X, CheckCircle, AlertCircle,
  Clock, ArrowUpDown, ArrowUp, ArrowDown, Lock, ExternalLink,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';

// ─── Date helpers ─────────────────────────────────────────────────────────────

const DATE_PRESETS = [
  { label: 'Today',     value: 'today'   },
  { label: 'This Week', value: 'week'    },
  { label: 'This Month',value: 'month'   },
  { label: 'Quarter',   value: 'quarter' },
  { label: 'This Year', value: 'year'    },
  { label: 'All Time',  value: 'all'     },
];

const getDateRange = (preset) => {
  const today = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const todayStr = fmt(today);

  switch (preset) {
    case 'today':
      return { startDate: todayStr, endDate: todayStr };
    case 'week': {
      const s = new Date(today);
      s.setDate(today.getDate() - today.getDay());
      return { startDate: fmt(s), endDate: todayStr };
    }
    case 'month':
      return { startDate: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-01`, endDate: todayStr };
    case 'quarter': {
      const qMonth = Math.floor(today.getMonth() / 3) * 3;
      return { startDate: `${today.getFullYear()}-${pad(qMonth + 1)}-01`, endDate: todayStr };
    }
    case 'year':
      return { startDate: `${today.getFullYear()}-01-01`, endDate: todayStr };
    default:
      return { startDate: '', endDate: '' };
  }
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

const getPeriodLabel = (preset, dateFrom, dateTo) => {
  const now = new Date();
  if (preset === 'today') return `Today · ${formatDate(now.toISOString())}`;
  if (preset === 'week') return 'This Week';
  if (preset === 'month') return now.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  if (preset === 'quarter') return `Q${Math.ceil((now.getMonth() + 1) / 3)} ${now.getFullYear()}`;
  if (preset === 'year') return String(now.getFullYear());
  if (preset === 'custom' && dateFrom && dateTo) return `${formatDate(dateFrom)} → ${formatDate(dateTo)}`;
  return 'All Time';
};

// ─── Currency ─────────────────────────────────────────────────────────────────

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

// ─── Status badge ─────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const map = {
    fully_paid:   { label: 'Fully Paid',   cls: 'bg-green-100 text-green-700',  Icon: CheckCircle },
    partial_paid: { label: 'Partial Paid', cls: 'bg-yellow-100 text-yellow-700', Icon: Clock },
    pending:      { label: 'Pending',      cls: 'bg-slate-100 text-slate-600',   Icon: Clock },
    overdue:      { label: 'Overdue',      cls: 'bg-red-100 text-red-700',       Icon: AlertCircle },
  };
  const { label, cls, Icon } = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon size={11} />{label}
    </span>
  );
};

// ─── Sort indicator ───────────────────────────────────────────────────────────

const SortIcon = ({ col, sortBy, sortDir }) => {
  if (sortBy !== col) return <ArrowUpDown size={13} className="text-slate-300 ml-1 inline" />;
  return sortDir === 'asc'
    ? <ArrowUp size={13} className="text-teal-600 ml-1 inline" />
    : <ArrowDown size={13} className="text-teal-600 ml-1 inline" />;
};

// ─── Empty form ───────────────────────────────────────────────────────────────

const EMPTY = { client_name: '', email: '', phone: '', visa_type: '', product: '', total_amount: '', amount_received: '', payment_status: 'pending', due_date: '', payment_date: '', notes: '' };

// ─── Main component ───────────────────────────────────────────────────────────

export default function SalesManagement() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading]           = useState(true);
  const [sales, setSales]               = useState([]);
  const [filtered, setFiltered]         = useState([]);
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [datePreset, setDatePreset]     = useState('month');
  const [dateFrom, setDateFrom]         = useState(() => getDateRange('month').startDate);
  const [dateTo, setDateTo]             = useState(() => getDateRange('month').endDate);
  const [sortBy, setSortBy]             = useState('created_at');
  const [sortDir, setSortDir]           = useState('desc');
  const [visaPrograms, setVisaPrograms] = useState([]);

  // modal
  const [showModal, setShowModal]       = useState(false);
  const [editingSale, setEditingSale]   = useState(null);
  const [formData, setFormData]         = useState({ ...EMPTY });
  const [saving, setSaving]             = useState(false);

  // stats
  const [stats, setStats] = useState({ total: 0, collected: 0, pending: 0, overdue: 0, count: 0 });

  // ── fetch visa programs once
  useEffect(() => {
    visaProgramAPI.getPrograms().then(r => setVisaPrograms(r.data || [])).catch(() => {});
  }, []);

  // ── fetch sales when date/status filter changes
  useEffect(() => { fetchSales(); }, [dateFrom, dateTo, statusFilter]); // eslint-disable-line

  const fetchSales = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom)     params.start_date     = dateFrom;
      if (dateTo)       params.end_date       = dateTo;
      if (statusFilter) params.payment_status = statusFilter;
      const res = await accountingAPI.getSales(params);
      setSales(res.data);
      calcStats(res.data);
    } catch {
      toast.error('Failed to load sales data');
    } finally {
      setLoading(false);
    }
  };

  const calcStats = (data) => {
    const today = new Date().toISOString().split('T')[0];
    setStats({
      count:     data.length,
      total:     data.reduce((s, r) => s + (r.total_amount || 0), 0),
      collected: data.reduce((s, r) => s + (r.amount_received || 0), 0),
      pending:   data.filter(r => (r.balance_due || 0) > 0 && r.payment_status !== 'overdue' && !(r.due_date && r.due_date < today))
                     .reduce((s, r) => s + (r.balance_due || 0), 0),
      overdue:   data.filter(r => (r.balance_due || 0) > 0 && (r.payment_status === 'overdue' || (r.due_date && r.due_date < today)))
                     .reduce((s, r) => s + (r.balance_due || 0), 0),
    });
  };

  // ── search filter + sort
  useEffect(() => {
    let list = [...sales];

    // search
    if (search.trim()) {
      const t = search.toLowerCase();
      list = list.filter(s =>
        s.client_name?.toLowerCase().includes(t) ||
        s.visa_type?.toLowerCase().includes(t) ||
        s.ucin?.toLowerCase().includes(t)
      );
    }

    // sort
    list.sort((a, b) => {
      let av = a[sortBy] ?? '';
      let bv = b[sortBy] ?? '';
      if (sortBy === 'total_amount' || sortBy === 'amount_received' || sortBy === 'balance_due') {
        av = Number(av); bv = Number(bv);
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

    setFiltered(list);
  }, [sales, search, sortBy, sortDir]);

  // ── sorting
  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  // ── date preset pills
  const applyPreset = (preset) => {
    setDatePreset(preset);
    const { startDate, endDate } = getDateRange(preset);
    setDateFrom(startDate);
    setDateTo(endDate);
  };

  // ── open modal
  const openAdd = () => {
    setEditingSale(null);
    setFormData({ ...EMPTY });
    setShowModal(true);
  };

  const openEdit = (sale) => {
    setEditingSale(sale);
    setFormData({
      client_name:     sale.client_name     || '',
      email:           sale.email           || '',
      phone:           sale.phone           || '',
      visa_type:       sale.visa_type       || '',
      product:         sale.product         || '',
      total_amount:    sale.total_amount    || '',
      amount_received: sale.amount_received || '',
      payment_status:  sale.payment_status  || 'pending',
      due_date:        sale.due_date        || '',
      payment_date:    sale.payment_date    || '',
      notes:           sale.notes           || '',
    });
    setShowModal(true);
  };

  const handleChange = (k, v) => setFormData(p => ({ ...p, [k]: v }));

  // ── save
  const handleSave = async () => {
    if (!formData.client_name || !formData.total_amount) {
      toast.error('Client name and total amount are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        total_amount:    parseFloat(formData.total_amount)    || 0,
        amount_received: parseFloat(formData.amount_received) || 0,
      };

      // for auto-captured, preserve locked fields from the original record
      if (editingSale?.is_auto_captured) {
        payload.client_name  = editingSale.client_name;
        payload.visa_type    = editingSale.visa_type;
        payload.product      = editingSale.product;
        payload.total_amount = editingSale.total_amount;
      }

      if (editingSale) {
        await accountingAPI.updateSale(editingSale.id, payload);
        toast.success('Sale updated');
      } else {
        await accountingAPI.createSale(payload);
        toast.success('Sale created');
      }
      setShowModal(false);
      fetchSales();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── delete
  const handleDelete = async (sale) => {
    if (sale.is_auto_captured) {
      toast.error('Auto-captured sales cannot be deleted. Edit the case instead.');
      return;
    }
    if (!window.confirm('Delete this sale record?')) return;
    try {
      await accountingAPI.deleteSale(sale.id);
      toast.success('Sale deleted');
      fetchSales();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // ── CSV export (includes Sale Date)
  const handleExport = () => {
    const headers = ['Sale Date', 'Client', 'Product/Visa', 'UCIN', 'Total', 'Received', 'Balance', 'Status', 'Due Date', 'Payment Date'];
    const rows = filtered.map(s => [
      formatDate(s.created_at),
      `"${s.client_name}"`,
      `"${s.visa_type || s.product || ''}"`,
      s.ucin || '',
      s.total_amount,
      s.amount_received,
      s.balance_due,
      s.payment_status,
      s.due_date || '',
      s.payment_date || '',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const a   = Object.assign(document.createElement('a'), {
      href:     URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: `sales_${datePreset}_${new Date().toISOString().split('T')[0]}.csv`,
    });
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const isAutoEdit = editingSale?.is_auto_captured;

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="sales-management">
      <Sidebar />
      <div className="flex-1 flex flex-col ml-[260px]">
        <TopHeader />
        <main className="flex-1 p-6 overflow-auto">

          {/* ── Header ── */}
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/accounting')}>
              <ChevronLeft size={18} />
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-800">Sales Management</h1>
              <p className="text-sm text-slate-500">Track and manage all sales records</p>
            </div>
            <Button variant="outline" size="sm" className="gap-2" onClick={handleExport}>
              <Download size={16} /> Export CSV
            </Button>
            <Button className="gap-2 bg-teal-600 hover:bg-teal-700" onClick={openAdd}>
              <Plus size={16} /> Add Manual Sale
            </Button>
          </div>

          {/* ── Stats ── */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Sales',   value: fmt(stats.total),     sub: `${stats.count} records`,   color: 'text-slate-800' },
              { label: 'Collected',     value: fmt(stats.collected),  sub: 'amount received',          color: 'text-green-600' },
              { label: 'Pending',       value: fmt(stats.pending),    sub: 'balance outstanding',      color: 'text-yellow-600' },
              { label: 'Overdue',       value: fmt(stats.overdue),    sub: 'past due date',            color: 'text-red-600' },
            ].map(({ label, value, sub, color }) => (
              <Card key={label} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500 mb-1">{label}</p>
                  <p className={`text-xl font-bold ${color}`}>{value}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* ── Filter bar ── */}
          <Card className="border-0 shadow-sm mb-4">
            <CardContent className="p-4 space-y-3">

              {/* Date preset pills */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide mr-1">Sale Date</span>
                {DATE_PRESETS.map(p => (
                  <button
                    key={p.value}
                    onClick={() => applyPreset(p.value)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                      datePreset === p.value
                        ? 'bg-teal-600 text-white border-teal-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-600'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
                {/* Custom range */}
                <span className="text-slate-300 text-xs mx-1">|</span>
                <Input
                  type="date" value={dateFrom}
                  onChange={e => { setDateFrom(e.target.value); setDatePreset('custom'); }}
                  className="h-7 w-32 text-xs"
                />
                <span className="text-slate-400 text-xs">to</span>
                <Input
                  type="date" value={dateTo}
                  onChange={e => { setDateTo(e.target.value); setDatePreset('custom'); }}
                  className="h-7 w-32 text-xs"
                />
              </div>

              {/* Search + status */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px] relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    placeholder="Search client, product, UCIN…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>
                <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-40 h-9">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="fully_paid">Fully Paid</SelectItem>
                    <SelectItem value="partial_paid">Partial Paid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
                {(statusFilter || datePreset === 'custom') && (
                  <Button variant="ghost" size="sm" onClick={() => { setStatusFilter(''); applyPreset('month'); }}>
                    <X size={14} className="mr-1" /> Clear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ── Table ── */}
          <Card className="border-0 shadow-sm">
            {/* Table context line */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <p className="text-sm text-slate-500">
                <span className="font-semibold text-slate-800">{filtered.length}</span>
                {' '}record{filtered.length !== 1 ? 's' : ''} &middot; <span className="text-teal-600 font-medium">{getPeriodLabel(datePreset, dateFrom, dateTo)}</span>
              </p>
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <Calendar size={12} /> Sorted by {sortBy === 'created_at' ? 'Sale Date' : sortBy === 'total_amount' ? 'Amount' : sortBy} {sortDir === 'desc' ? '↓' : '↑'}
              </div>
            </div>

            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                  <DollarSign size={40} className="mx-auto mb-3 text-slate-200" />
                  <p className="font-medium">No sales records found</p>
                  <p className="text-xs mt-1">Try changing the date range or filters</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-500 uppercase">
                        {/* Sale Date — sortable */}
                        <th
                          className="text-left py-3 px-4 cursor-pointer select-none hover:text-teal-600 whitespace-nowrap"
                          onClick={() => toggleSort('created_at')}
                        >
                          Sale Date <SortIcon col="created_at" sortBy={sortBy} sortDir={sortDir} />
                        </th>
                        <th className="text-left py-3 px-4 whitespace-nowrap">Client</th>
                        <th className="text-left py-3 px-4 whitespace-nowrap">Product / Visa</th>
                        {/* Total — sortable */}
                        <th
                          className="text-right py-3 px-4 cursor-pointer select-none hover:text-teal-600 whitespace-nowrap"
                          onClick={() => toggleSort('total_amount')}
                        >
                          Total <SortIcon col="total_amount" sortBy={sortBy} sortDir={sortDir} />
                        </th>
                        {/* Received — sortable */}
                        <th
                          className="text-right py-3 px-4 cursor-pointer select-none hover:text-teal-600 whitespace-nowrap"
                          onClick={() => toggleSort('amount_received')}
                        >
                          Received <SortIcon col="amount_received" sortBy={sortBy} sortDir={sortDir} />
                        </th>
                        {/* Balance — sortable */}
                        <th
                          className="text-right py-3 px-4 cursor-pointer select-none hover:text-teal-600 whitespace-nowrap"
                          onClick={() => toggleSort('balance_due')}
                        >
                          Balance <SortIcon col="balance_due" sortBy={sortBy} sortDir={sortDir} />
                        </th>
                        <th className="text-center py-3 px-4 whitespace-nowrap">Status</th>
                        <th className="text-left py-3 px-4 whitespace-nowrap">Due Date</th>
                        <th className="text-left py-3 px-4 whitespace-nowrap">UCIN</th>
                        <th className="text-center py-3 px-4 whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filtered.map(sale => (
                        <tr key={sale.id} className="hover:bg-slate-50 transition-colors">

                          {/* Sale Date */}
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span className="text-slate-700 font-medium">{formatDate(sale.created_at)}</span>
                          </td>

                          {/* Client */}
                          <td className="py-3 px-4">
                            <div className="font-medium text-slate-800">{sale.client_name}</div>
                            {sale.is_auto_captured && (
                              <span className="inline-flex items-center gap-0.5 text-[10px] text-indigo-500 font-medium">
                                <Lock size={9} /> Linked to Case
                              </span>
                            )}
                          </td>

                          {/* Product */}
                          <td className="py-3 px-4 text-slate-600 max-w-[180px] truncate">
                            {sale.visa_type || sale.product || '—'}
                          </td>

                          {/* Amounts */}
                          <td className="py-3 px-4 text-right font-semibold text-slate-800">{fmt(sale.total_amount)}</td>
                          <td className="py-3 px-4 text-right text-green-600">{fmt(sale.amount_received)}</td>
                          <td className="py-3 px-4 text-right">
                            <span className={(sale.balance_due || 0) > 0 ? 'text-orange-600 font-medium' : 'text-slate-400'}>
                              {fmt(sale.balance_due)}
                            </span>
                          </td>

                          {/* Status */}
                          <td className="py-3 px-4 text-center">
                            <StatusBadge status={sale.payment_status} />
                          </td>

                          {/* Due date */}
                          <td className="py-3 px-4 text-sm text-slate-500 whitespace-nowrap">
                            {sale.due_date
                              ? <span className={sale.due_date < new Date().toISOString().split('T')[0] && (sale.balance_due || 0) > 0 ? 'text-red-600 font-medium' : ''}>
                                  {formatDate(sale.due_date)}
                                </span>
                              : '—'}
                          </td>

                          {/* UCIN */}
                          <td className="py-3 px-4">
                            {sale.ucin
                              ? <button className="text-sm text-teal-600 hover:underline font-medium" onClick={() => sale.case_id && navigate(`/admin/cases/${sale.case_id}`)}>
                                  {sale.ucin}
                                </button>
                              : '—'}
                          </td>

                          {/* Actions */}
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-1">
                              {sale.case_id && (
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="View Case"
                                  onClick={() => navigate(`/admin/cases/${sale.case_id}`)}>
                                  <Eye size={15} className="text-slate-500" />
                                </Button>
                              )}
                              {/* Edit — always enabled; auto-captured gets a partial-edit modal */}
                              <Button
                                variant="ghost" size="sm" className="h-8 w-8 p-0" title={sale.is_auto_captured ? 'Edit Payment Details' : 'Edit'}
                                onClick={() => openEdit(sale)}
                              >
                                <Edit2 size={15} className={sale.is_auto_captured ? 'text-indigo-400' : 'text-slate-500'} />
                              </Button>
                              <Button
                                variant="ghost" size="sm" className="h-8 w-8 p-0" title={sale.is_auto_captured ? 'Cannot delete — linked to case' : 'Delete'}
                                onClick={() => handleDelete(sale)}
                              >
                                <Trash2 size={15} className={sale.is_auto_captured ? 'text-slate-200' : 'text-red-400'} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>

      {/* ── Add / Edit Modal ── */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isAutoEdit ? (
                <>
                  <Lock size={16} className="text-indigo-400" />
                  Edit Payment Details
                </>
              ) : (
                editingSale ? 'Edit Sale' : 'Add Manual Sale'
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">

            {/* Auto-captured info banner */}
            {isAutoEdit && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <Lock size={14} className="text-indigo-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-indigo-700 mb-0.5">Auto-captured from Case {editingSale?.ucin}</p>
                    <p className="text-xs text-indigo-600">Client name, visa type and total amount are locked — they are managed from the case. You can update payment details below.</p>
                    {editingSale?.case_id && (
                      <button
                        className="inline-flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 font-medium mt-1"
                        onClick={() => { setShowModal(false); navigate(`/admin/cases/${editingSale.case_id}`); }}
                      >
                        <ExternalLink size={11} /> Open Case to edit amount
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Locked read-only fields — shown for auto-captured */}
            {isAutoEdit ? (
              <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">Client Name</p>
                  <p className="text-sm font-semibold text-slate-700">{editingSale?.client_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">Visa / Product</p>
                  <p className="text-sm font-semibold text-slate-700 truncate">{editingSale?.visa_type || editingSale?.product || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">Total Amount</p>
                  <p className="text-sm font-bold text-slate-800">{fmt(editingSale?.total_amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-0.5">Sale Date</p>
                  <p className="text-sm text-slate-700">{formatDate(editingSale?.created_at)}</p>
                </div>
              </div>
            ) : (
              /* Full form for manual sales */
              <>
                <div>
                  <Label>Client Name *</Label>
                  <Input value={formData.client_name} onChange={e => handleChange('client_name', e.target.value)} placeholder="Enter client name" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Client Email</Label>
                    <Input type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)} placeholder="client@example.com" />
                    <p className="text-[10px] text-slate-400 mt-0.5">Used to auto-create client profile</p>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={formData.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="+91 98765 43210" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Product / Visa Type</Label>
                    <Select value={formData.visa_type || ''} onValueChange={v => handleChange('visa_type', v === 'custom' ? '' : v)}>
                      <SelectTrigger><SelectValue placeholder="Select visa type" /></SelectTrigger>
                      <SelectContent>
                        {visaPrograms.map(p => (
                          <SelectItem key={p.id} value={`${p.country} - ${p.name}`}>{p.country} - {p.name}</SelectItem>
                        ))}
                        <SelectItem value="custom">Other (Custom)</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.visa_type === '' && (
                      <Input value={formData.product || ''} onChange={e => handleChange('product', e.target.value)} placeholder="Enter custom product/service" className="mt-2" />
                    )}
                  </div>
                  <div>
                    <Label>Total Amount *</Label>
                    <Input type="number" value={formData.total_amount} onChange={e => handleChange('total_amount', e.target.value)} placeholder="0" />
                  </div>
                </div>
              </>
            )}

            {/* ── Editable payment fields — shown for both manual and auto-captured ── */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount Received</Label>
                <Input type="number" value={formData.amount_received} onChange={e => handleChange('amount_received', e.target.value)} placeholder="0" />
              </div>
              <div>
                <Label>Payment Status</Label>
                <Select value={formData.payment_status} onValueChange={v => handleChange('payment_status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partial_paid">Partial Paid</SelectItem>
                    <SelectItem value="fully_paid">Fully Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={formData.due_date} onChange={e => handleChange('due_date', e.target.value)} />
              </div>
              <div>
                <Label>Payment Received Date</Label>
                <Input type="date" value={formData.payment_date} onChange={e => handleChange('payment_date', e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={formData.notes} onChange={e => handleChange('notes', e.target.value)} placeholder="Optional notes…" rows={2} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              {saving ? 'Saving…' : editingSale ? 'Save Changes' : 'Create Sale'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
